import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';
import { cn } from '../../lib/utils';

/**
 * Admin control that turns Ava on or off for one user. Saves immediately
 * (PUT /api/users/:id/ava-access); Ava is on for everyone unless turned off here.
 */
export function AvaAccessToggle({
  userId,
  enabled: initial,
  onChange,
  className
}: {
  userId: string;
  enabled: boolean;
  onChange?: (enabled: boolean) => void;
  className?: string;
}) {
  const [enabled, setEnabled] = useState(initial);
  const [saving, setSaving] = useState(false);

  // List endpoints don't always include avaEnabled, so read the real state.
  useEffect(() => {
    let live = true;
    apiClient.get<{ success: boolean; data: { avaEnabled: boolean } }>(`/users/${userId}/ava-access`)
      .then(res => { if (live && res?.success) setEnabled(res.data.avaEnabled); })
      .catch(() => {});
    return () => { live = false; };
  }, [userId]);

  async function toggle() {
    const next = !enabled;
    setSaving(true);
    try {
      await apiClient.put(`/users/${userId}/ava-access`, { enabled: next });
      setEnabled(next);
      onChange?.(next);
      toast.success(next ? 'Ava turned on for this user' : 'Ava turned off for this user');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not update Ava access');
    } finally {
      setSaving(false);
    }
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={toggle}
      disabled={saving}
      title={enabled ? 'Ava is on for this user' : 'Ava is off for this user'}
      className={cn(
        'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60',
        enabled ? 'border-brand/40 bg-brand-muted text-brand-dark' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50',
        className
      )}
    >
      <Sparkles className="h-4 w-4" />
      Ava
      <span className={cn('relative inline-flex h-4 w-7 items-center rounded-full transition-colors', enabled ? 'bg-brand' : 'bg-gray-300')}>
        <span className={cn('inline-block h-3 w-3 rounded-full bg-white shadow transition-transform', enabled ? 'translate-x-3.5' : 'translate-x-0.5')} />
      </span>
    </button>
  );
}
